import moment, { Moment } from 'moment'

export type DateLike = string | number | Date | null | undefined

/**
 * returns a {@link moment} object for the supplied {@link dateLike} argument
 *
 * if `null` or `undefined` are supplied, `null` will be returned
 *
 * @param dateLike
 */
export function toMoment(dateLike: DateLike) {
  if (!dateLike) return null
  return moment(dateLike)
}

/**
 * executes {@link toMoment} on every provided item in {@link dateLikes}
 *
 * this can result in a list containing {@link moment} objects and `undefined`
 *
 * @param dateLikes
 */
export function toMoments(
  dateLikes: DateLike[]
): [Moment | null, Moment | null] | null {
  if (dateLikes.every((dateLike) => !dateLike)) return null
  const [first, second] = dateLikes.map((dateLike) => toMoment(dateLike))
  return [first, second]
}

type Period = 'month' | 'year' | 'week'

/**
 * builds a moment date range based on today for use in date range pickers
 *
 * @param period
 * @param options {@link StartEndOptions}
 * @example
 * const thisMonth = startEndRange('month')
 * const lastWeek = startEndRange('week', { subtract: 1 })
 * const last28Days = startEndRange('day',{ subtract: { first: 28 } })
 */
export function startEndRange(
  period: 'day' | Period,
  options?: StartEndOptions
): [moment.Moment, moment.Moment] {
  const { subtract, add } = options ?? {}
  const start = moment()
  const end = moment()
  if (add && subtract) throw new Error("Can't add and subtract from a date")
  else if (subtract) {
    const { first, last } =
      typeof subtract === 'number'
        ? { first: subtract, last: subtract }
        : subtract
    start.subtract(first, period)
    end.subtract(last, period)
  } else if (add) {
    const { first, last } =
      typeof add === 'number' ? { first: add, last: add } : add
    start.add(first, period)
    end.add(last, period)
  }
  return [start.startOf(period), end.endOf(period)]
}

interface StartEndOptions {
  /**
   * subtract from the {@link startEndRange|period}
   */
  subtract?: number | SubtractOptions
  /**
   * add from the {@link startEndRange|period}
   */
  add?: number | AddOptions
}

interface AddOptions {
  /**
   * the first amount to add to the {@link startEndRange|period}
   */
  first: number
  /**
   * the second amount to add to the {@link startEndRange|period}
   */
  last?: number
}

interface SubtractOptions {
  /**
   * the first amount to subtract from the {@link startEndRange|period}
   */
  first: number
  /**
   * the second amount to subtract from the {@link startEndRange|period}
   */
  last?: number
}

/**
 * builds a moment date range to get the last period's worth, up til today, last
 * period
 *
 * @param period
 * @example
 * const [startOfLastMonth, endOfTodayLastMonth] = lastPeriodToDay('month')
 */
export function lastPeriodToDay(
  period: Period
): [moment.Moment, moment.Moment] {
  return [
    moment().subtract(1, period).startOf(period),
    moment().subtract(1, period).endOf('day'),
  ]
}

/**
 * builds a moment date range to get the next period's worth, up til today, next
 * period
 *
 * @param period
 */
export function nextPeriodToDay(
  period: Period
): [moment.Moment, moment.Moment] {
  return [
    moment().add(1, period).startOf(period),
    moment().add(1, period).endOf('day'),
  ]
}

/**
 * builds a moment date range to get this period's worth, up til today
 */
export function thisPeriodToDay(
  period: Period
): [moment.Moment, moment.Moment] {
  return [moment().startOf(period), moment().endOf('day')]
}
